import type {
    RecordSurveyImpressionInput,
    RecordSurveySeenInput,
    RecordSurveySubmittedInput,
} from '../../../../__generated__/types.gqlgenerated';

import {
    RecordSurveyImpressionDocument,
    type RecordSurveyImpressionMutation,
    type RecordSurveyImpressionMutationVariables,
} from '../../../graphQL/mutations/__generated__/recordSurveyImpression.gqlgenerated';
import {
    RecordSurveySeenDocument,
    type RecordSurveySeenMutation,
    type RecordSurveySeenMutationVariables,
} from '../../../graphQL/mutations/__generated__/recordSurveySeen.gqlgenerated';
import {
    RecordSurveySubmittedDocument,
    type RecordSurveySubmittedMutation,
    type RecordSurveySubmittedMutationVariables,
} from '../../../graphQL/mutations/__generated__/recordSurveySubmitted.gqlgenerated';

// Polyfill must be imported before uuid
import 'react-native-get-random-values';

import { useApolloClient } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

/** Current survey version — bump when survey format changes */
const SURVEY_VERSION = '1.0.0';

/** Surface ID for RN homepage job feed */
const SURFACE_ID = 'job_feed_RN';

export interface UseSurveyLoggingOptions {
    jobKey: string;
    trackingKey?: string;
    position?: number;
}

export interface SurveyLoggingActions {
    /** Returns the stable survey ID for this instance */
    getSurveyId: () => string;
    /** Records that the survey was rendered (impression) - call on mount */
    recordImpression: () => void;
    /** Records that the survey was scrolled into viewport - call on visibility */
    recordSeen: () => void;
    /** Records that the user submitted a rating (with optional feedback) */
    recordSubmitted: (rating: number, additionalFeedback?: string) => void;
}

/**
 * Hook to handle survey event logging via OneGraph mutations.
 *
 * Events:
 * - Impression: fires on mount (survey rendered on page)
 * - Seen: fires when survey scrolls into viewport (first time only)
 * - Submitted: fires when user submits rating (debounced auto-submit or button press)
 */
export const useSurveyLogging = ({
    jobKey,
    trackingKey,
    position,
}: UseSurveyLoggingOptions): SurveyLoggingActions => {
    const client = useApolloClient();

    // Generate a stable UUID for this survey instance
    const surveyIdRef = useRef<string>(uuidv4());
    const impressionFiredRef = useRef(false);
    const seenFiredRef = useRef(false);

    const getSurveyId = useCallback(() => surveyIdRef.current, []);

    const recordImpression = useCallback(() => {
        // Fire only once per survey instance
        if (impressionFiredRef.current) return;
        impressionFiredRef.current = true;

        const input: RecordSurveyImpressionInput = {
            surveyId: surveyIdRef.current,
            aggJobKey: jobKey,
            surfaceId: SURFACE_ID,
            jobPosition: position ?? -1,
            rjpTk: trackingKey ?? '',
            version: SURVEY_VERSION,
        };

        client
            .mutate<RecordSurveyImpressionMutation, RecordSurveyImpressionMutationVariables>({
                mutation: RecordSurveyImpressionDocument,
                variables: { input },
            })
            .catch((error) => {
                console.error('[RelevanceSurvey] Failed to record impression:', error);
            });
    }, [client, jobKey, trackingKey, position]);

    const recordSeen = useCallback(() => {
        // Fire only once per survey instance
        if (seenFiredRef.current) return;
        seenFiredRef.current = true;

        const input: RecordSurveySeenInput = {
            surveyId: surveyIdRef.current,
            aggJobKey: jobKey,
        };

        client
            .mutate<RecordSurveySeenMutation, RecordSurveySeenMutationVariables>({
                mutation: RecordSurveySeenDocument,
                variables: { input },
            })
            .catch((error) => {
                console.error('[RelevanceSurvey] Failed to record seen:', error);
            });
    }, [client, jobKey]);

    const recordSubmitted = useCallback(
        (rating: number, additionalFeedback?: string) => {
            const input: RecordSurveySubmittedInput = {
                surveyId: surveyIdRef.current,
                aggJobKey: jobKey,
                rating,
                additionalFeedback: additionalFeedback || undefined,
            };

            client
                .mutate<RecordSurveySubmittedMutation, RecordSurveySubmittedMutationVariables>({
                    mutation: RecordSurveySubmittedDocument,
                    variables: { input },
                })
                .catch((error) => {
                    console.error('[RelevanceSurvey] Failed to record submission:', error);
                });
        },
        [client, jobKey],
    );

    return {
        getSurveyId,
        recordImpression,
        recordSeen,
        recordSubmitted,
    };
};
